"use strict";
import { jsx, jsxs } from "react/jsx-runtime";
import React from "react";
import { onAmountDrop } from "./amount_drop";
import Api from "../../api/api";
import ExtractedFields from "../factoring/extracted_fields";
import ExtractedFieldVisibility from "./extracted_field_visibility";
import InvoiceExpectedAmount from "./invoice_expected_amount";
import moment from "moment";
import PossibleAdjustments from "./possible_adjustments";
import ValueLinkTextDroppableInput from "../value_link_text_droppable_input";
import generalFormatter from "../../formatters/general";
import quickpayHelper from "../../helpers/quickpay";
import lineItemsForCalculation from "../data/line_items_for_calculation";
import TextDroppableInput from "../text_droppable_input";
import _ from "underscore";
function stateFromProps(props) {
  const invoice = props.carrierInvoice;
  return {
    customer: props.load.load.customer,
    source_invoice_number: props.source_invoice_number,
    source_invoice_amount: props.source_invoice_amount,
    source_invoice_date: props.source_invoice_date,
    invoice_disposition: props.invoice_disposition,
    showingNoteField: false,
    forceShowInvoiceInformation: false,
    extracted_fields: invoice ? invoice.extracted_fields : {}
  };
}
function infoItem({ title, inputEl, showCurrency = false }) {
  let currencyUnit, inputClass, dataLeadingText;
  if (showCurrency) {
    currencyUnit = "USD";
    inputClass = "v3__input-wrapper--has-leading-text";
    dataLeadingText = "$";
  }
  return /* @__PURE__ */ jsxs(
    "label",
    {
      className: "v3__input-label",
      style: {
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-xxxs)"
      },
      children: [
        /* @__PURE__ */ jsxs(
          "div",
          {
            className: "v3__input-label-text",
            style: { display: "flex", justifyContent: "space-between" },
            children: [
              /* @__PURE__ */ jsx("span", { children: title }),
              /* @__PURE__ */ jsx(
                "span",
                {
                  className: "v3__type__extra-small-body",
                  style: { color: "var(--color-grayscale-600)" },
                  children: currencyUnit
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsx(
          "div",
          {
            className: `v3__input-wrapper ${inputClass}`,
            style: { fontSize: "16px" },
            "data-leading-text": dataLeadingText,
            children: inputEl
          }
        )
      ]
    }
  );
}
function quickpayDiscountMinimum() {
  return quickpayHelper.quickpayMinimum();
}
function invoiceAmount(invoice) {
  return generalFormatter.cleanAmount(invoice.source_invoice_amount) || 0;
}
function accountSettings() {
  return Api.Account.currentSettings();
}
function onInvoiceAmountDragOver(e) {
  e.preventDefault();
}
function requiredExtractedFieldNames(visibleExtractedFields2) {
  return _.chain(visibleExtractedFields2).filter((field) => field.required === true).pluck("name").value();
}
function cleanedInvoiceNumberIfDifferent(props) {
  if (!props.cleanedSourceInvoiceNumber || !props.carrierInvoice) {
    return;
  }
  if (props.cleanedSourceInvoiceNumber != props.carrierInvoice.source_invoice_number) {
    return /* @__PURE__ */ jsxs("div", { className: "load-detail__invoice-document-info-note", children: [
      "Your TMS will be sent ",
      props.cleanedSourceInvoiceNumber
    ] });
  }
}
function shouldHideInformationSection(props, state) {
  return props.carrierInvoice.using_implicit_carrier_invoice && !state.forceShowInvoiceInformation;
}
function noteKeyPressNotifier(props, event) {
  var _a;
  (_a = props.noteFieldChanged) == null ? void 0 : _a.call(props, event.target.value);
}
function showNotesField(props, state) {
  return props.showNoteFieldByDefault || state.showingNoteField || !_.isEmpty(props.currentNote);
}
function notePlaceholder(props) {
  if (props.showNoteFieldByDefault) {
    return "Please give a reason why rules were ignored";
  }
}
function quickpayWouldBeGreaterThanZero(props) {
  return quickpayDiscountAmount(props) > 0;
}
function quickpayDiscountAmount(props) {
  const adjustedTotal = tmsTotal(props) - otherInvoicesAdjustment(props) - otherAdjustment(props);
  var discountAmount = 0;
  if (adjustedTotal > 0) {
    discountAmount = quickpayDiscountPercentage(props) * adjustedTotal / 100;
    if (quickpayDiscountMinimum() > discountAmount) {
      discountAmount = quickpayDiscountMinimum();
    }
  }
  return discountAmount;
}
function otherInvoicesAdjustment(props) {
  if (hasOtherInvoiceAdjustments(props)) {
    return _.chain(otherInvoices(props)).map(invoiceAmount).reduce((memo, num) => memo + num, 0).value();
  }
  return 0;
}
function quickpayDiscountPercentage(props) {
  return quickpayHelper.quickpayPercentage(props.carrier);
}
function hasOtherInvoiceAdjustments(props) {
  return isDraftStatus(props) && otherInvoices(props).length > 0;
}
function isDraftStatus(props) {
  return props.carrierInvoice.state === "draft";
}
function otherInvoices(props) {
  return _.chain(props.load.carrier_invoices).filter((ci) => ci.id !== props.carrierInvoice.id).filter((ci) => ci.state !== "deleted").filter((ci) => ci.state !== "abandoned").filter((ci) => invoiceAmount(ci) > 0).value();
}
function otherAdjustment(props) {
  if (hasAdjustments(props)) {
    return _.chain(adjustments(props)).pluck("amount").map(parseFloat).reduce((memo, num) => memo + num, 0).value();
  }
  return 0;
}
function hasAdjustments(props) {
  return !_.isEmpty(adjustments(props));
}
function adjustments(props) {
  return props.load.load.tms_adjustments;
}
function possiblePossibleAdjustments(props) {
  if (props.carrierInvoice.quickpay_disposition === "applied_to_invoice") {
    return /* @__PURE__ */ jsx(
      PossibleAdjustments,
      {
        expectedTotal: tmsTotal(props),
        load: props.load,
        carrierInvoice: props.carrierInvoice
      }
    );
  }
}
function quickpayDisposition(props) {
  return props.carrierInvoice.quickpay_disposition || "not_quickpay";
}
function updateQuickpayRelatedFields(props, hasQuickpay, amountToPay, quickpayDisposition2) {
  var _a;
  const fields = ["amount_to_pay", "quickpay_disposition", "quickpay"];
  const values = [amountToPay, quickpayDisposition2, hasQuickpay];
  (_a = props.invoiceFieldsChanged) == null ? void 0 : _a.call(props, fields, values);
}
function loadLineItemsPresent(props) {
  return props.load.load.line_items.length > 0;
}
function tmsTotal(props) {
  const alwaysUseCarrierCharge = accountSettings().always_use_carrier_charge_for_expected_amount;
  const calculationLineItems = lineItemsForCalculation(
    props.load.load.line_items,
    accountSettings().ignore_adjustment_line_items
  );
  const lineItemsForCarrier = _.select(
    calculationLineItems,
    (lineItem) => props.carrierInvoice.carrier_id === lineItem.tms_carrier_id
  );
  if (alwaysUseCarrierCharge || !loadLineItemsPresent(props)) {
    return carrierCharge(props);
  }
  if (lineItemsForCarrier.length > 0) {
    return _.reduce(
      lineItemsForCarrier,
      (memo, lineItem) => {
        return memo + parseFloat(lineItem.total);
      },
      0
    );
  }
  return 0;
}
function carrierCharge(props) {
  if (props.load.load.carrier_charge) {
    return parseFloat(props.load.load.carrier_charge);
  }
  return 0;
}
function visibleExtractedFields(props) {
  const settings = Api.Account.currentSettings() || {};
  const allFields = settings.carrier_invoice_extracted_fields || [];
  const load = props.load.load;
  return _.chain(allFields).filter((f) => {
    const strategy = ExtractedFieldVisibility.strategyForField(f);
    return strategy(load);
  }).value();
}
function possibleExpectedAmount(props) {
  if (props.load.load.source_system !== "hubtran") {
    return /* @__PURE__ */ jsx("div", { className: "load-detail__document-information-item", children: /* @__PURE__ */ jsx(
      InvoiceExpectedAmount,
      {
        sourceInvoiceAmount: props.source_invoice_amount,
        expectedTotal: tmsTotal(props),
        quickpayDiscountAmount: quickpayDiscountAmount(props),
        useCarrierCharge: accountSettings().always_use_carrier_charge_for_expected_amount,
        carrierInvoice: props.carrierInvoice,
        load: props.load
      }
    ) });
  }
}
function possibleQuickpaySelect(props, forReskin) {
  if (accountSettings().quickpayDiscount && quickpayWouldBeGreaterThanZero(props) && !props.readOnly) {
    const className = forReskin ? "show_for_reskin" : "show_for_non_reskin";
    return /* @__PURE__ */ jsxs(
      "select",
      {
        className: "load-detail__quickpay-select " + className,
        onChange: (event) => handleQuickpayChange(props, event),
        value: quickpayDisposition(props),
        children: [
          /* @__PURE__ */ jsx("option", { value: "not_quickpay", children: " Not QuickPay" }),
          /* @__PURE__ */ jsxs("option", { value: "applied_to_invoice", children: [
            " ",
            "Invoice total includes QuickPay discount"
          ] }),
          /* @__PURE__ */ jsxs("option", { value: "not_applied_to_invoice", children: [
            " ",
            "Invoice total excludes QuickPay discount"
          ] })
        ]
      }
    );
  }
}
function handleQuickpayChange(props, event) {
  let amount;
  if (event.target.value === "not_applied_to_invoice") {
    amount = props.carrierInvoice.amount_to_pay || props.carrierInvoice.source_invoice_amount;
    let discounted_amount = amount - quickpayDiscountAmount(props);
    if (discounted_amount < 0) {
      discounted_amount = 0;
    }
    updateQuickpayRelatedFields(
      props,
      true,
      discounted_amount,
      "not_applied_to_invoice"
    );
  } else if (event.target.value === "applied_to_invoice") {
    amount = props.carrierInvoice.source_invoice_amount;
    updateQuickpayRelatedFields(props, true, amount, "applied_to_invoice");
  } else {
    amount = props.carrierInvoice.source_invoice_amount;
    updateQuickpayRelatedFields(props, false, amount, null);
  }
}
const DocumentInformation = React.createClass({
  getInitialState: function() {
    return stateFromProps(this.props);
  },
  componentWillReceiveProps: function(newProps) {
    if (newProps.carrierInvoice.id !== this.props.carrierInvoice.id) {
      this.setState(stateFromProps(newProps));
    }
  },
  render: function() {
    if (this.props.showInternalNoteV3Reskin) {
      return this.possibleNoteFieldV3Reskin();
    } else if (this.props.v3ReskinEnabled) {
      return this.documentInformationSectionV3Reskin();
    } else {
      return /* @__PURE__ */ jsxs("div", { "data-component": "DocumentInformation", children: [
        this.possibleNoteField(),
        /* @__PURE__ */ jsx("h3", { className: "load-detail__section-header" }),
        this.documentInformationSection()
      ] });
    }
  },
  documentInformationSection: function() {
    if (shouldHideInformationSection(this.props, this.state)) {
      return this.showInformationSectionButton();
    } else {
      return /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("div", { className: "load-detail__quickpay-select-container", children: possibleQuickpaySelect(this.props, true) }),
        /* @__PURE__ */ jsxs("div", { className: "load-detail__document-information", children: [
          /* @__PURE__ */ jsxs("div", { className: "load-detail__document-information-item", children: [
            /* @__PURE__ */ jsxs("div", { className: "load-detail__invoice-document-info-label", children: [
              /* @__PURE__ */ jsx("label", { children: "Invoice Amount" }),
              possibleQuickpaySelect(this.props, false)
            ] }),
            /* @__PURE__ */ jsxs("div", { className: "row collapse", children: [
              /* @__PURE__ */ jsx("div", { className: "small-1 columns", children: /* @__PURE__ */ jsx("span", { className: "prefix", children: "$" }) }),
              /* @__PURE__ */ jsxs("div", { className: "small-9 columns", children: [
                this.invoiceInput(
                  "source_invoice_amount",
                  "Total Charge",
                  "text",
                  "amount"
                ),
                possiblePossibleAdjustments(this.props)
              ] })
            ] })
          ] }),
          possibleExpectedAmount(this.props),
          /* @__PURE__ */ jsxs("div", { className: "load-detail__document-information-item", children: [
            /* @__PURE__ */ jsxs("div", { className: "load-detail__invoice-document-info-label", children: [
              /* @__PURE__ */ jsx("label", { children: "Invoice Number" }),
              this.possibleInvoiceDisposition()
            ] }),
            /* @__PURE__ */ jsxs("span", { className: "load-detail__invoice-document-info-input-and-note-wrapper", children: [
              cleanedInvoiceNumberIfDifferent(this.props),
              this.invoiceInput(
                "source_invoice_number",
                "Invoice Number",
                "text"
              )
            ] })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "load-detail__document-information-item", children: [
            /* @__PURE__ */ jsx("div", { className: "load-detail__invoice-document-info-label", children: /* @__PURE__ */ jsx("label", { children: "Invoice Date" }) }),
            /* @__PURE__ */ jsxs("div", { className: "row collapse", children: [
              /* @__PURE__ */ jsx("div", { className: "small-7 columns", children: this.dateDroppableInvoiceInput(
                "source_invoice_date",
                "Invoice Date"
              ) }),
              /* @__PURE__ */ jsx("div", { className: "small-3 columns", children: /* @__PURE__ */ jsx(
                "a",
                {
                  onClick: this.setInvoiceDateToToday,
                  className: "load-detail__today-button expand",
                  disabled: this.props.readOnly,
                  children: "Today"
                }
              ) })
            ] })
          ] })
        ] }),
        this.possibleExtractedFields()
      ] });
    }
  },
  documentInformationSectionV3Reskin: function() {
    if (shouldHideInformationSection(this.props, this.state)) {
      return this.showInformationSectionButton();
    } else {
      const rowPairs = [
        [
          infoItem({
            title: "Invoice amount",
            inputEl: this.invoiceInput(
              "source_invoice_amount",
              "Total Charge",
              "text",
              "amount",
              true
            ),
            showCurrency: true
          }),
          infoItem({
            title: "Invoice number",
            inputEl: this.invoiceInput(
              "source_invoice_number",
              "Invoice Number",
              "text"
            )
          })
        ]
      ];
      return /* @__PURE__ */ jsxs(
        "div",
        {
          style: {
            display: "flex",
            flexDirection: "column",
            gap: "var(--space-xs)"
          },
          children: [
            /* @__PURE__ */ jsx("h3", { children: "Invoice details" }),
            rowPairs.map((pair, i) => /* @__PURE__ */ jsxs(
              "div",
              {
                style: {
                  display: "flex",
                  gap: "var(--space-s)"
                },
                children: [
                  /* @__PURE__ */ jsx("div", { style: { flex: "1" }, children: pair[0] }),
                  /* @__PURE__ */ jsx("div", { style: { flex: "1" }, children: pair[1] })
                ]
              },
              i
            ))
          ]
        }
      );
    }
  },
  possibleInvoiceDisposition: function() {
    var _a;
    const account = Api.Account.current();
    return /* @__PURE__ */ jsx(
      "div",
      {
        className: "load-detail__invoice-disposition-select",
        style: { display: account.tms === "3Gtms" ? "block" : "none" },
        children: /* @__PURE__ */ jsxs(
          "select",
          {
            onChange: this.handleInvoiceDispositionChange,
            value: (_a = this.state.invoice_disposition) != null ? _a : "",
            children: [
              /* @__PURE__ */ jsx("option", { value: "original_invoice", children: "Original invoice" }),
              /* @__PURE__ */ jsx("option", { value: "supplemental_invoice", children: "Supplemental invoice" })
            ]
          }
        )
      }
    );
  },
  showInformationSectionButton: function() {
    const component = this;
    return /* @__PURE__ */ jsxs("div", { className: "load-detail__manual-details", children: [
      "Payment Amount copied from expected due to no invoice document.",
      " ",
      /* @__PURE__ */ jsx(
        "a",
        {
          onClick: () => {
            component.setState({ forceShowInvoiceInformation: true });
          },
          children: "Enter Invoice Details Manually ..."
        }
      )
    ] });
  },
  possibleNoteField: function() {
    const component = this;
    if (this.props.readOnly) {
      return "";
    }
    if (showNotesField(this.props, this.state)) {
      return /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("h3", { className: "load-detail__section-header", children: "Add an Internal Note" }),
        /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(
          "textarea",
          {
            className: "load-detail__textarea",
            placeholder: notePlaceholder(this.props),
            onChange: (event) => noteKeyPressNotifier(this.props, event)
          }
        ) })
      ] });
    }
    return /* @__PURE__ */ jsx("p", { children: /* @__PURE__ */ jsx(
      "a",
      {
        className: "load-detail__link",
        onClick: () => {
          component.setState({ showingNoteField: true });
        },
        children: "Add an Internal Note"
      }
    ) });
  },
  possibleNoteFieldV3Reskin: function() {
    if (this.props.readOnly) {
      return null;
    }
    if (showNotesField(this.props, this.state)) {
      return /* @__PURE__ */ jsxs(
        "div",
        {
          style: {
            display: "flex",
            flexDirection: "column",
            gap: "4px"
          },
          children: [
            /* @__PURE__ */ jsx("div", { className: "v3__type__extra-small-body-semi-bold", children: "Internal note" }),
            /* @__PURE__ */ jsx(
              "textarea",
              {
                className: "v3__input",
                style: { height: "64px" },
                placeholder: notePlaceholder(this.props) || "Add a note",
                onChange: (event) => noteKeyPressNotifier(this.props, event)
              }
            )
          ]
        }
      );
    }
    return /* @__PURE__ */ jsx(
      "a",
      {
        className: "v3__type__extra-small-body-semi-bold",
        onClick: () => {
          this.setState({ showingNoteField: true });
        },
        children: "Add internal note"
      }
    );
  },
  handleInvoiceDispositionChange: function(event) {
    if (event.target.value == "original_invoice" || event.target.value == "supplemental_invoice") {
      this.setState({ invoice_disposition: event.target.value }, () => {
        const fields = ["disposition"];
        const values = [this.state.invoice_disposition];
        this.props.invoiceFieldsChanged(fields, values);
      });
    }
  },
  setInvoiceDateToToday: function(e) {
    e.preventDefault();
    const today = moment(/* @__PURE__ */ new Date()).format("YYYY-MM-DD");
    this.setState({ source_invoice_date: today });
    this.props.invoiceFieldChanged("source_invoice_date", today);
    this.props.invoiceChangeComplete();
  },
  invoiceInput: function(field, placeholder, type, ref, hasLeadingText = false) {
    const { value, requestChange } = this.myLinkState(field);
    return /* @__PURE__ */ jsx(
      ValueLinkTextDroppableInput,
      {
        type,
        ref,
        disabled: this.props.readOnly,
        value,
        onValueChange: requestChange,
        onBlur: this.props.invoiceChangeComplete,
        onKeyPress: this.props.keyPressNotifier(field),
        inputDropped: this.dropTracker(field, this.props.document),
        onDragOver: onInvoiceAmountDragOver,
        onDrop: onAmountDrop,
        placeholder,
        v3ReskinEnabled: this.props.v3ReskinEnabled,
        hasLeadingText
      }
    );
  },
  dropTracker: function(field, document) {
    if (this.props.readOnly) {
      return () => true;
    } else {
      return this.makeDropped(field, document);
    }
  },
  makeDropped: function(field, document) {
    const component = this;
    const tracker = Api.DropTracker.createForDocumentAndField(document, field);
    return (chars, position) => {
      const newState = {};
      let value = chars;
      if (field === "source_invoice_amount") {
        value = generalFormatter.cleanAmount(value);
        if (value === null || value.toString() === "NaN") {
          value = null;
        }
      }
      newState[field] = value;
      component.setState(newState);
      tracker(chars, position);
    };
  },
  onInvoiceDateDrop(field) {
    return (e, value) => {
      e.target.value = value || e.target.value;
      this.setState({ [field]: e.target.value });
      this.props.invoiceFieldChanged(field, e.target.value);
      this.props.invoiceChangeComplete();
    };
  },
  dateDroppableInvoiceInput: function(field, placeholder) {
    const { value, requestChange } = this.myLinkState(field);
    return /* @__PURE__ */ jsx(
      TextDroppableInput,
      {
        type: "date",
        ref: "date",
        disabled: this.props.readOnly,
        value,
        onValueChange: requestChange,
        onDrop: this.onInvoiceDateDrop(field),
        onKeyPress: this.props.keyPressNotifier(field),
        placeholder
      }
    );
  },
  myLinkState: function(field) {
    const component = this;
    const fields = [
      "source_invoice_number",
      "source_invoice_amount",
      "source_invoice_date"
    ];
    let val = fields.includes(field) ? this.state[field] : this.props[field];
    if (this.props.readOnly) {
      val = this.props[field];
    }
    return {
      value: val,
      requestChange: function(value) {
        var vl;
        if (_.contains(fields, field)) {
          const state = {};
          state[field] = value;
          component.setState(state);
        }
        if (field === "source_invoice_amount") {
          const flds = ["amount_to_pay", "source_invoice_amount"];
          if (value) {
            const fmtd = generalFormatter.cleanAmount(value);
            vl = fmtd ? fmtd.toString() : "0.00";
          } else {
            vl = "0.00";
          }
          let values;
          if (component.props.carrierInvoice.quickpay_disposition === "not_applied_to_invoice") {
            let amountToPay = (vl - quickpayDiscountAmount(component.props)).toFixed(2);
            if (amountToPay < 0) {
              amountToPay = 0;
            }
            values = [amountToPay, vl];
          } else {
            values = [vl, vl];
          }
          component.props.invoiceFieldsChanged(flds, values);
        } else {
          component.props.invoiceFieldChanged(field, value);
        }
      }
    };
  },
  possibleExtractedFields() {
    const invoice = this.props.carrierInvoice;
    if (invoice) {
      const visibleFields = visibleExtractedFields(this.props);
      const requiredExtractedFields = requiredExtractedFieldNames(visibleFields);
      return /* @__PURE__ */ jsx(
        ExtractedFields,
        {
          sourceId: invoice.id,
          extractedFields: visibleFields,
          requiredFields: requiredExtractedFields,
          fieldsSourceObject: invoice && invoice.extracted_fields || {},
          renderRows: false,
          fieldContainerClass: "load-detail__document-information-item",
          containerClass: "load-detail__document-information-extracted-fields",
          onChange: this.extractedFieldsChanged
        }
      );
    }
  },
  extractedFieldsChanged(updatedFields) {
    this.setState({ extractedFields: updatedFields });
    this.props.invoiceFieldsChanged(["extracted_fields"], [updatedFields]);
  }
});
export default DocumentInformation;
